import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  Divider,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Business,
  Star,
  CheckCircle,
  ShoppingCart,
  ArrowBack,
  Download,
  CompareArrows,
  Info
} from '@mui/icons-material';
import api from '../services/api';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand } from '../theme/colors';

const QuoteComparisonPage = () => {
  const { quoteRequestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [quoteRequest, setQuoteRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('line_items'); // 'side_by_side', 'line_items', 'total_only'
  const [selectedItems, setSelectedItems] = useState({}); // {quoteId: [itemIds]}
  const [cartItems, setCartItems] = useState([]);
  const [quoteDetailsDialog, setQuoteDetailsDialog] = useState({ open: false, quote: null });

  useEffect(() => {
    if (quoteRequestId) {
      loadQuoteRequest();
      loadQuotes();
    }
  }, [quoteRequestId]);

  // Load cart when quote request is loaded
  useEffect(() => {
    if (quoteRequest?.project_id) {
      loadCart();
    }
  }, [quoteRequest?.project_id]);

  const loadCart = async () => {
    try {
      const response = await api.get(`/material-orders/cart?projectId=${quoteRequest?.project_id}`);

      if (response.data.success) {
        const items = response.data.data.cartItems || [];
        setCartItems(items);
      }
    } catch (err) {
      console.error('Error loading cart:', err);
    }
  };

  const loadQuoteRequest = async () => {
    try {
      const response = await api.get(`/merchant-quotes/requests/${quoteRequestId}`);

      if (response.data.success) {
        setQuoteRequest(response.data.data);
      }
    } catch (err) {
      console.error('Error loading quote request:', err);
    }
  };

  const loadQuotes = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/merchant-quotes/requests/${quoteRequestId}/quotes`);

      if (response.data.success) {
        const quotesData = response.data.data || [];
        // Ensure each quote has items array and each item has an id
        const quotesWithItems = quotesData.map(quote => ({
          ...quote,
          items: (quote.items || []).map((item, index) => ({
            ...item,
            id: item.id || `temp-${quote.id}-${index}` // Fallback ID if missing
          }))
        }));
        console.log('Loaded quotes with items:', quotesWithItems);
        setQuotes(quotesWithItems);
      }
    } catch (err) {
      console.error('Error loading quotes:', err);
      setError('Failed to load quotes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemSelection = (quoteId, itemId) => {
    setSelectedItems(prev => {
      const quoteItems = prev[quoteId] || [];
      const isSelected = quoteItems.includes(itemId);

      if (isSelected) {
        // Remove from selection
        return {
          ...prev,
          [quoteId]: quoteItems.filter(id => id !== itemId)
        };
      } else {
        // Add to selection
        return {
          ...prev,
          [quoteId]: [...quoteItems, itemId]
        };
      }
    });
  };

  const addToCart = async (quoteId, item) => {
    try {
      setError(''); // Clear any previous errors

      if (!quoteRequest?.project_id) {
        setError('Project ID is missing. Please refresh the page.');
        return;
      }

      // Get current cart with timeout
      const cartResponse = await api.get(
        `/material-orders/cart?projectId=${quoteRequest.project_id}`,
        { timeout: 10000 } // 10 second timeout
      );

      // Parse cart items - handle both cartItems and cart_items (from database)
      let currentCart = [];
      if (cartResponse.data.success) {
        const cartData = cartResponse.data.data;
        if (cartData.cartItems) {
          currentCart = Array.isArray(cartData.cartItems) ? cartData.cartItems : [];
        } else if (cartData.cart_items) {
          // If cart_items is a JSONB string, parse it
          if (typeof cartData.cart_items === 'string') {
            try {
              currentCart = JSON.parse(cartData.cart_items);
            } catch (e) {
              currentCart = [];
            }
          } else if (Array.isArray(cartData.cart_items)) {
            currentCart = cartData.cart_items;
          }
        } else if (cartData.isEmpty) {
          currentCart = [];
        }
      }

      // Check if item is already in cart
      const existingItem = currentCart.find(
        ci => ci.quote_item_id === item.id && ci.quote_id === quoteId
      );
      if (existingItem) {
        setError('Item is already in your cart');
        return;
      }

      // Find the quote to get merchant info
      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) {
        setError('Quote not found');
        return;
      }

      // Check if quote is expired
      const isExpired = quote.status === 'expired' || 
        (quote.valid_until && new Date(quote.valid_until) < new Date());
      
      if (isExpired) {
        setError('This quote has expired. Please request a new quote from the merchant.');
        return;
      }

      // Validate item has an id
      if (!item.id) {
        console.error('Item missing id:', item);
        setError('Item ID is missing. Please refresh the page and try again.');
        return;
      }

      // Add new item - include quote-level fees for proper total calculation
      const newCartItem = {
        quote_id: quoteId,
        quote_item_id: item.id,
        merchant_id: quote.merchant_id,
        merchant_name: quote.merchant_name,
        material_name: item.material_name,
        quantity: item.requested_quantity,
        unit: item.requested_unit,
        unit_price: item.unit_price,
        total_price: item.total_price,
        merchant_product_name: item.merchant_product_name,
        // Store quote-level fees (these are per quote, not per item)
        delivery_fee: quote.delivery_fee || 0,
        offloading_fee: quote.offloading_fee || 0,
        other_charges: quote.other_charges || 0,
        vat_percentage: quote.vat_percentage || 0,
        vat_amount: quote.vat_amount || 0
      };

      console.log('Adding to cart:', { quoteId, itemId: item.id, newCartItem });

      const updatedCart = [...currentCart, newCartItem];

      // Update cart with timeout
      const updateResponse = await api.post(
        '/material-orders/cart',
        {
          projectId: quoteRequest.project_id,
          cartItems: updatedCart
        },
        { timeout: 10000 } // 10 second timeout
      );

      if (updateResponse.data.success) {
        // Update local state
        setCartItems(updatedCart);
        toggleItemSelection(quoteId, item.id);
        console.log('Successfully added to cart! Cart now has', updatedCart.length, 'items');
      } else {
        const errorMsg = updateResponse.data.error || 'Failed to add item to cart';
        console.error('Cart update failed:', updateResponse.data);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.error || err.message || 'Failed to add item to cart. Please try again.');
    }
  };

  const removeFromCart = async (itemIndex) => {
    try {
      const updatedCart = cartItems.filter((_, index) => index !== itemIndex);

      await api.post('/material-orders/cart', {
        projectId: quoteRequest?.project_id,
        cartItems: updatedCart
      });

      setCartItems(updatedCart);
    } catch (err) {
      console.error('Error removing from cart:', err);
    }
  };

  const calculateCartTotal = () => {
    // Group by merchant and calculate totals
    const merchantTotals = {};
    cartItems.forEach(item => {
      if (!merchantTotals[item.merchant_id]) {
        merchantTotals[item.merchant_id] = {
          merchant_name: item.merchant_name,
          subtotal: 0,
          items: []
        };
      }
      merchantTotals[item.merchant_id].subtotal += parseFloat(item.total_price || 0);
      merchantTotals[item.merchant_id].items.push(item);
    });
    return merchantTotals;
  };

  const getBestPrice = (materialName) => {
    const matchingItems = quotes.flatMap(quote =>
      (quote.items || []).filter(item => item.material_name === materialName)
    );

    if (matchingItems.length === 0) return null;

    return matchingItems.reduce((best, item) => {
      const itemPrice = parseFloat(item.total_price || 0);
      const bestPrice = parseFloat(best.total_price || 0);
      return itemPrice < bestPrice ? item : best;
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && quotes.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  const cartTotals = calculateCartTotal();
  const grandTotal = Object.values(cartTotals).reduce((sum, merchant) => sum + merchant.subtotal, 0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Supplier Quotes Received
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      {quoteRequest && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f5f7fa' }}>
          <Typography variant="body2" color="text.secondary">
            Project: <strong>{quoteRequest.project_title}</strong> | Request ID: {quoteRequestId}
          </Typography>
        </Paper>
      )}

      {quotes.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No quotes received yet. Quotes typically arrive within 24-72 hours.
        </Alert>
      ) : (
        <>
          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="side_by_side">Side-by-Side</ToggleButton>
              <ToggleButton value="line_items">Line Items</ToggleButton>
              <ToggleButton value="total_only">Total Only</ToggleButton>
            </ToggleButtonGroup>
            <Button startIcon={<Download />} variant="outlined" size="small">
              Download All
            </Button>
          </Box>

          {/* Quotes Comparison */}
          {viewMode === 'line_items' && (
            <Box sx={{ mb: 4 }}>
              {quoteRequest && (
                <MaterialItemComparison
                  quotes={quotes}
                  quoteRequest={quoteRequest}
                  onAddToCart={addToCart}
                  selectedItems={selectedItems}
                  getBestPrice={getBestPrice}
                  onViewDetails={(quote) => setQuoteDetailsDialog({ open: true, quote })}
                />
              )}
            </Box>
          )}

          {/* Cart Summary */}
          {cartItems.length > 0 && (
            <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'primary.light' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Your Cart
              </Typography>

              {Object.entries(cartTotals).map(([merchantId, merchant]) => (
                <Box key={merchantId} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    FROM: {merchant.merchant_name}
                  </Typography>
                  {merchant.items.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">
                        • {item.material_name} ({item.quantity} {item.unit})
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ${parseFloat(item.total_price).toFixed(2)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeFromCart(cartItems.findIndex(ci => ci.quote_item_id === item.quote_item_id))}
                        >
                          ×
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                    Subtotal from this supplier: ${merchant.subtotal.toFixed(2)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  CART TOTAL:
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ${grandTotal.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => setCartItems([])}>
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<ShoppingCart />}
                  onClick={() => navigate(`/checkout?projectId=${quoteRequest?.project_id}&quoteRequestId=${quoteRequestId}`)}
                  sx={{
                    background: brand.gradient,
                    '&:hover': {
                      background: brand.gradientHover
                    }
                  }}
                >
                  Proceed to Checkout
                </Button>
              </Box>
            </Paper>
          )}

          {/* Tip */}
          <Alert severity="info">
            <strong>💡 TIP:</strong> You can order different items from different suppliers to get the best overall deal. Delivery fees apply per supplier.
          </Alert>
        </>
      )}

      {/* Quote Details Dialog */}
      <Dialog
        open={quoteDetailsDialog.open}
        onClose={() => setQuoteDetailsDialog({ open: false, quote: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setQuoteDetailsDialog({ open: false, quote: null })} />
        <DialogTitle>
          Quote Details - {quoteDetailsDialog.quote?.merchant_name}
        </DialogTitle>
        <DialogContent>
          {quoteDetailsDialog.quote && (
            <QuoteDetailsView quote={quoteDetailsDialog.quote} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDetailsDialog({ open: false, quote: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Material Item Comparison Component
const MaterialItemComparison = ({ quotes, quoteRequest, onAddToCart, selectedItems, getBestPrice, onViewDetails }) => {
  // Get unique materials from all quotes
  const allMaterials = [...new Set(quotes.flatMap(quote => 
    (quote.items || []).map(item => item.material_name)
  ))];

  return (
    <Box>
      {allMaterials.map((materialName) => {
        const matchingQuotes = quotes.map(quote => ({
          quote,
          item: (quote.items || []).find(item => item.material_name === materialName)
        })).filter(({ item }) => item);

        const bestPrice = getBestPrice(materialName);

        return (
          <Paper key={materialName} elevation={2} sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Item: {materialName}
            </Typography>

            {matchingQuotes.map(({ quote, item }) => {
              const isBestPrice = bestPrice && bestPrice.id === item.id;
              const isInCart = selectedItems[quote.id]?.includes(item.id);
              const isExpired = quote.status === 'expired' || 
                (quote.valid_until && new Date(quote.valid_until) < new Date());

              return (
                <Box key={`${quote.id}-${item.id}`} sx={{ mb: 2, p: 2, bgcolor: isExpired ? 'error.light' : 'grey.50', borderRadius: 1, border: isExpired ? '1px solid' : 'none', borderColor: isExpired ? 'error.main' : 'transparent' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Business sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {quote.merchant_name}
                        </Typography>
                        {isExpired && (
                          <Chip label="EXPIRED" size="small" color="error" />
                        )}
                        {!isExpired && quote.verification_tier === 'Tier3' && (
                          <Chip label="PREMIUM" size="small" color="success" />
                        )}
                        {!isExpired && <Rating value={quote.rating_average || 0} readOnly size="small" />}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Product: {item.merchant_product_name || item.material_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.requested_quantity} {item.requested_unit} | 
                        Unit Price: ${parseFloat(item.unit_price).toFixed(2)} | 
                        Total: ${parseFloat(item.total_price).toFixed(2)}
                        {isBestPrice && !isExpired && <Chip label="💰 BEST" size="small" color="success" sx={{ ml: 1 }} />}
                      </Typography>
                      {isExpired && (
                        <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                          This quote has expired. Please request a new quote from this merchant.
                        </Alert>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Availability: {item.in_stock ? 'In stock' : 'Out of stock'} | 
                        Lead Time: {item.lead_time_days || 'N/A'} days
                      </Typography>
                      {item.item_notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Notes: {item.item_notes}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onViewDetails(quote)}
                      >
                        View Details
                      </Button>
                      <Button
                        size="small"
                        variant={isInCart ? "contained" : "outlined"}
                        startIcon={<ShoppingCart />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Add to Cart button clicked:', { 
                            quoteId: quote.id, 
                            itemId: item.id, 
                            item: item,
                            hasId: !!item.id,
                            inStock: item.in_stock
                          });
                          if (!item.id) {
                            console.error('Item missing ID:', item);
                            alert('Error: Item ID is missing. Please refresh the page.');
                            return;
                          }
                          onAddToCart(quote.id, item);
                        }}
                        disabled={!item.in_stock || !item.id || (quote.status === 'expired' || (quote.valid_until && new Date(quote.valid_until) < new Date()))}
                      >
                        {isInCart ? 'Added to Cart' : 
                         (quote.status === 'expired' || (quote.valid_until && new Date(quote.valid_until) < new Date())) 
                           ? 'Quote Expired' 
                           : `Add to Cart - $${parseFloat(item.total_price || 0).toFixed(2)}`}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {matchingQuotes.length === 0 && (
              <Alert severity="warning">
                ⚠️ No quotes available for this item
              </Alert>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

// Quote Details View Component
const QuoteDetailsView = ({ quote }) => {
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Merchant Information</Typography>
        <Typography variant="body2">{quote.merchant_name}</Typography>
        <Typography variant="body2">Rating: {quote.rating_average ? parseFloat(quote.rating_average).toFixed(1) : '0.0'} ({quote.total_ratings || 0} ratings)</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Quote Items</Typography>
      {quote.items?.map((item, index) => (
        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.material_name}</Typography>
          <Typography variant="body2">Quantity: {item.requested_quantity} {item.requested_unit}</Typography>
          <Typography variant="body2">Unit Price: ${parseFloat(item.unit_price).toFixed(2)}</Typography>
          <Typography variant="body2">Total: ${parseFloat(item.total_price).toFixed(2)}</Typography>
          {item.item_notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Notes: {item.item_notes}
            </Typography>
          )}
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Subtotal:</Typography>
        <Typography>${parseFloat(quote.subtotal || 0).toFixed(2)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Delivery Fee:</Typography>
        <Typography>${parseFloat(quote.delivery_fee || 0).toFixed(2)}</Typography>
      </Box>
      {parseFloat(quote.offloading_fee || 0) > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Offloading Fee:</Typography>
          <Typography>${parseFloat(quote.offloading_fee || 0).toFixed(2)}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Other Charges:</Typography>
        <Typography>${parseFloat(quote.other_charges || 0).toFixed(2)}</Typography>
      </Box>
      {parseFloat(quote.vat_amount || 0) > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>VAT {quote.vat_percentage ? `(${parseFloat(quote.vat_percentage).toFixed(1)}%)` : ''}:</Typography>
          <Typography>${parseFloat(quote.vat_amount || 0).toFixed(2)}</Typography>
        </Box>
      )}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Total:</Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          ${parseFloat(quote.total_amount || 0).toFixed(2)}
        </Typography>
      </Box>

      {quote.merchant_notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Merchant Notes:</Typography>
          <Typography variant="body2">{quote.merchant_notes}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuoteComparisonPage;

